import { useCallback, useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { Alert, Button, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import * as icons from "react-bootstrap-icons";
import { toast } from "react-toastify";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import { Footer } from "../../Footer";
import { usePagedList } from "../../hooks/usePagedList";
import {
  PlayerGetEmailSettings,
  PlayerGetManagerCounts,
  PlayerGetManagers,
  PlayerUpdateManagerEmails,
} from "../../redux/auth-reducer/auth-service";
import { readApiMessage, setStoredEmailVerified } from "../../utils/helper/helper";
import { PLAYER_VERIFY_MESSAGE } from "../../utils/helper/emailVerification";
import { useEmailVerified } from "../../hooks/useEmailVerification";
import {
  CANCELLED_MESSAGE,
  MANAGERS_PAGE_SIZE,
  MANAGER_STATUS,
  MANAGER_TABS,
  headerCheckboxState,
  isDimmed,
  lockReason,
  selectedLabel,
  selectionOf,
  sentMessage,
  showingLabel,
  toggleSelection,
} from "../../utils/helper/managers";
import { ALL_EMAILS_OFF_TOOLTIP, managerEmailsLabel } from "../../utils/helper/unsubscribe";
import { ManagerIdentity } from "../../CommonComponent/Managers/ManagerIdentity";
import { SubscribeConfirmModal } from "../../CommonComponent/Managers/SubscribeConfirmModal";
import { CancelRequestModal } from "../../CommonComponent/Managers/CancelRequestModal";

const PAGE_SIZES = [MANAGERS_PAGE_SIZE];
const SEARCH_DELAY_MS = 500;
const EMPTY_COUNTS = { allCount: 0, subscribedCount: 0, pendingCount: 0, availableCount: 0 };

/** The header "select all" box. indeterminate is a DOM property, not an attribute. */
const SelectAllCheckbox = ({ state, onChange }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state.indeterminate;
  }, [state.indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input manager-check"
      aria-label="Select all available managers"
      checked={state.checked}
      disabled={state.disabled}
      onChange={onChange}
    />
  );
};

/**
 * A row's checkbox. A locked one explains itself in a tooltip; it sits in a span because a disabled
 * input fires no mouse events of its own.
 */
const RowCheckbox = ({ manager, checked, onToggle }) => {
  const reason = lockReason(manager.status);
  const box = (
    <input
      type="checkbox"
      className="form-check-input manager-check"
      aria-label={`Select ${manager.fullName}`}
      checked={checked}
      disabled={Boolean(reason)}
      onChange={() => onToggle(manager)}
    />
  );
  if (!reason) return box;

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{reason}</Tooltip>}>
      <span className="manager-check-locked" tabIndex={0}>
        {box}
      </span>
    </OverlayTrigger>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`manager-status manager-status-${String(status).toLowerCase()}`}>{status}</span>
);

/**
 * A subscribed Manager's email switch on the My Managers tab. While "Emails from Managers" is off in
 * Account Settings it is greyed out but keeps showing its own value, which comes back into force
 * when that switch is turned on again; the tooltip says where to change it. It sits in a span
 * because a disabled input fires no mouse events of its own.
 *
 * Until the player verifies their email it is greyed out too, and the tooltip asks them to verify:
 * no Manager's email reaches them before that, whatever the switches say.
 */
const EmailsSwitch = ({ manager, allEmailsOff, emailUnverified, saving, onChange }) => {
  const locked = emailUnverified || allEmailsOff;
  const box = (
    <Form.Check
      type="switch"
      id={`manager-emails-${manager.managerId}`}
      className="manager-emails-switch"
      aria-label={managerEmailsLabel(manager.fullName)}
      checked={manager.broadcastEmails !== false}
      disabled={locked || saving}
      onChange={(event) => onChange(manager, event.target.checked)}
    />
  );
  if (!locked) return box;

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{emailUnverified ? PLAYER_VERIFY_MESSAGE : ALL_EMAILS_OFF_TOOLTIP}</Tooltip>}
    >
      <span className="manager-emails-locked" tabIndex={0}>
        {box}
      </span>
    </OverlayTrigger>
  );
};

/**
 * The player's Managers page (#133, PL-003): every active manager with the player's status, in
 * three tabs, and the only place - with the dashboard's Discover Managers widget - where a player
 * requests managers. Ticking a box never sends anything; requests go out from the confirmation
 * dialog only. The server decides what each row is (usp_GetAllPlayerManager); this page draws it.
 */
export const Managers = () => {
  const [data, setData] = useState();
  const [tabKey, setTabKey] = useState(MANAGER_TABS[0].key);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [countsKey, setCountsKey] = useState(0);
  const [selection, setSelection] = useState(() => new Map());
  const [selectingAll, setSelectingAll] = useState(false);
  const [confirmManagers, setConfirmManagers] = useState(null);
  const [cancelManager, setCancelManager] = useState(null);
  const [notice, setNotice] = useState("");
  // The general "Emails from Managers" switch (Account Settings); null until read, and then the
  // per-Manager switches stay usable.
  const [allEmails, setAllEmails] = useState(null);
  // Whether the player's email is verified, from the same read; null for an API that does not say,
  // and then nothing is locked for it.
  const [emailVerified, setEmailVerified] = useState(null);
  const storedVerified = useEmailVerified();
  const [savingEmails, setSavingEmails] = useState(() => new Set());

  const tab = MANAGER_TABS.find((t) => t.key === tabKey) || MANAGER_TABS[0];

  function sidebarfun() {
    setData(!data);
  }

  // The search is applied a moment after typing stops, not on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // A new fetchPage is a new filter, which usePagedList answers from page 1.
  const fetchPage = useCallback(
    ({ PageNumber, PageSize }) =>
      PlayerGetManagers({ SearchTerm: search, Status: tab.status, PageNumber, PageSize }),
    [search, tab.status]
  );
  const pager = usePagedList(fetchPage, PAGE_SIZES);

  // The counters follow the search.
  useEffect(() => {
    let current = true;
    PlayerGetManagerCounts(search).then((res) => {
      if (current && res?.data) setCounts({ ...EMPTY_COUNTS, ...res.data });
    });
    return () => {
      current = false;
    };
  }, [search, countsKey]);

  // A selection belongs to the results it was made in.
  useEffect(() => {
    setSelection(new Map());
  }, [tabKey, search]);

  useEffect(() => {
    let current = true;
    PlayerGetEmailSettings().then((res) => {
      if (!current || res?.data?.status !== true) return;
      setAllEmails(Boolean(res.data.broadcastEmails));
      if (typeof res.data.emailVerified === "boolean") {
        setEmailVerified(res.data.emailVerified);
        setStoredEmailVerified(res.data.emailVerified);
      }
    });
    return () => {
      current = false;
    };
  }, []);

  const setRowEmails = (managerId, emails) =>
    pager.updateRows((rows) =>
      rows.map((row) => (row.managerId === managerId ? { ...row, broadcastEmails: emails } : row))
    );

  // The row shows the change at once and goes back, with the server's message, if it is refused.
  const changeEmails = async (manager, emails) => {
    const id = manager.managerId;
    setRowEmails(id, emails);
    setSavingEmails((current) => new Set(current).add(id));
    const res = await PlayerUpdateManagerEmails(id, emails);
    setSavingEmails((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });

    if (res?.data?.status === true) return;
    setRowEmails(id, !emails);
    if (res) toast.error(readApiMessage(res));
  };

  const refresh = () => {
    pager.reload();
    setCountsKey((k) => k + 1);
  };

  // Only the All Managers tab can hold Available managers.
  const availableCount = tab.status === "All" ? counts.availableCount : 0;
  const headerState = headerCheckboxState(availableCount, selection.size);

  // Select all covers every Available manager in the current search, on every page, so their ids
  // are asked for in one page as large as the count.
  const toggleAll = async () => {
    if (headerState.checked || headerState.indeterminate) {
      setSelection(new Map());
      return;
    }
    setSelectingAll(true);
    const res = await PlayerGetManagers({
      SearchTerm: search,
      Status: MANAGER_STATUS.AVAILABLE,
      PageNumber: 1,
      PageSize: availableCount,
    });
    setSelectingAll(false);
    if (res?.data?.data?.dataList) setSelection(selectionOf(res.data.data.dataList));
    else if (res) toast.error(readApiMessage(res));
  };

  const toggleOne = (manager) => setSelection((current) => toggleSelection(current, manager));

  const handleSent = (result) => {
    setConfirmManagers(null);
    setSelection(new Map());
    refresh();
    if (result.sentCount > 0) setNotice(sentMessage(result, "Pending tab"));
    else toast.error(result.message);
  };

  const handleCancelled = (cancelled) => {
    setCancelManager(null);
    refresh();
    if (cancelled) setNotice(CANCELLED_MESSAGE);
  };

  const clearSearch = () => {
    setSearchText("");
    setSearch("");
  };

  // Rebuilt on every render on purpose: the cells read the current selection, and a page holds
  // six rows.
  const columns = [
    {
      name: (
        <SelectAllCheckbox
          state={{ ...headerState, disabled: headerState.disabled || selectingAll }}
          onChange={toggleAll}
        />
      ),
      cell: (row) => (
        <RowCheckbox manager={row} checked={selection.has(row.managerId)} onToggle={toggleOne} />
      ),
      width: "60px",
    },
    {
      name: <strong>Manager</strong>,
      cell: (row) => <ManagerIdentity manager={row} />,
      grow: 3,
    },
    // #212: the number of sports, not their names. The dash is for an API from before the count,
    // which sends no sportCount at all; a manager without sports shows 0, like the other counts.
    {
      name: <strong>Sports</strong>,
      selector: (row) => row.sportCount ?? "—",
      width: "100px",
    },
    { name: <strong>Tournaments</strong>, selector: (row) => row.tournamentCount, width: "120px" },
    { name: <strong>Auctions</strong>, selector: (row) => row.auctionCount, width: "100px" },
    { name: <strong>Players</strong>, selector: (row) => row.playerCount, width: "100px" },
    {
      name: <strong>Status</strong>,
      cell: (row) => <StatusBadge status={row.status} />,
      width: "130px",
    },
    // My Managers holds Subscribed Managers only, which have no action: the column is their email
    // switch instead.
    tab.key === "subscribed" ? {
      name: <strong>Emails</strong>,
      cell: (row) =>
        row.status === MANAGER_STATUS.SUBSCRIBED ? (
          <EmailsSwitch
            manager={row}
            allEmailsOff={allEmails === false}
            emailUnverified={emailVerified === false && storedVerified !== true}
            saving={savingEmails.has(row.managerId)}
            onChange={changeEmails}
          />
        ) : null,
      width: "130px",
    } : {
      name: <strong>Actions</strong>,
      cell: (row) =>
        row.status === MANAGER_STATUS.AVAILABLE ? (
          <Button size="sm" className="manager-action" onClick={() => setConfirmManagers([row])}>
            Subscribe
          </Button>
        ) : row.status === MANAGER_STATUS.PENDING ? (
          <Button
            size="sm"
            variant="outline-danger"
            className="manager-action"
            onClick={() => setCancelManager(row)}
          >
            Cancel
          </Button>
        ) : (
          // #303: Subscribed (on All Managers) and Declined have no action; a dash says so instead
          // of an empty cell.
          "—"
        ),
      width: "130px",
    },
  ];

  const rowStyles = [
    { when: (row) => selection.has(row.managerId), classNames: ["manager-row-selected"] },
    { when: (row) => isDimmed(row), classNames: ["manager-row-dimmed"] },
  ];

  const emptyState = search ? (
    <div className="manager-empty">
      <p>No managers match your search.</p>
      <Button variant="secondary" onClick={clearSearch}>
        Clear search
      </Button>
    </div>
  ) : tab.key === "subscribed" ? (
    <div className="manager-empty">
      <p>You have no managers yet. Select managers on the All Managers tab to request a subscription.</p>
      <Button onClick={() => setTabKey("all")}>Go to All Managers</Button>
    </div>
  ) : tab.key === "pending" ? (
    <div className="manager-empty">
      <p>You have no pending requests.</p>
    </div>
  ) : (
    <div className="manager-empty">
      <p>No managers to show yet.</p>
    </div>
  );

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div className={data ? "dashboard-content full-contnet" : "dashboard-content"}>
          <Row>
            <Col md={6}>
              <h1 className="heading-dash">Managers</h1>
            </Col>
          </Row>

          <div className="managers-card">
            {notice && (
              <Alert variant="success" dismissible onClose={() => setNotice("")} className="managers-notice">
                <icons.CheckCircleFill className="me-2" />
                {notice}
              </Alert>
            )}

            <div className="managers-sticky">
              <div className="managers-bar">
                <div className="managers-tabs" role="tablist">
                  {MANAGER_TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={t.key === tab.key}
                      className={`managers-tab${t.key === tab.key ? " active" : ""}`}
                      onClick={() => setTabKey(t.key)}
                    >
                      {t.label}
                      <span className="managers-tab-count">{counts[t.countField] ?? 0}</span>
                    </button>
                  ))}
                </div>
                <div className="search-input managers-search d-flex align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by manager or tournament..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {searchText && (
                    <button className="btn btn-clear" onClick={clearSearch} aria-label="Clear search">
                      <span>&times;</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Reserved even when empty, so the table never shifts when a manager is ticked. */}
              <div className="managers-selection-row">
                {selection.size > 0 && (
                  <>
                    <Button size="sm" onClick={() => setConfirmManagers([...selection.values()])}>
                      Subscribe
                    </Button>
                    <span className="managers-selected-count">{selectedLabel(selection.size)}</span>
                    <Button size="sm" variant="link" onClick={() => setSelection(new Map())}>
                      Clear
                    </Button>
                  </>
                )}
              </div>
            </div>

            <DataTable
              columns={columns}
              data={pager.rows}
              keyField="managerId"
              conditionalRowStyles={rowStyles}
              noDataComponent={pager.loading ? <div className="manager-empty" /> : emptyState}
            />

            {pager.totalRecords > 0 && (
              <div className="managers-pager">
                <span>{showingLabel(pager.page, pager.perPage, pager.totalRecords)}</span>
                <div className="pagination-controls">
                  <Button onClick={pager.previousPage} disabled={pager.page <= 1} aria-label="Previous page">
                    <icons.ArrowLeft />
                  </Button>
                  <span>
                    {" "}
                    {pager.page} of {Math.max(pager.totalPages, 1)}{" "}
                  </span>
                  <Button
                    onClick={pager.nextPage}
                    disabled={pager.page >= pager.totalPages}
                    aria-label="Next page"
                  >
                    <icons.ArrowRight />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>

      <SubscribeConfirmModal
        show={Boolean(confirmManagers)}
        managers={confirmManagers}
        onCancel={() => setConfirmManagers(null)}
        onSent={handleSent}
      />
      <CancelRequestModal
        show={Boolean(cancelManager)}
        manager={cancelManager}
        onClose={() => setCancelManager(null)}
        onDone={handleCancelled}
      />
    </>
  );
};
